/**
 * Helpers for DATE-only values ('YYYY-MM-DD' strings, no time component).
 *
 * Never feed those to `new Date(str)` before rendering: JS parses them as
 * UTC midnight, which displays as the PREVIOUS day (with a bogus 6:00 PM)
 * in any US timezone. And never derive "today's date" via
 * `new Date().toISOString().split('T')[0]`: after ~6pm Denver that is
 * tomorrow's UTC date. These helpers do both operations safely.
 */

/**
 * Parse a 'YYYY-MM-DD' (or ISO-prefixed) value as a LOCAL date at midnight.
 * Falls back to `new Date(value)` for anything that isn't date-only shaped
 * (real datetimes parse correctly there).
 */
export function parseDateOnly(value) {
  if (value instanceof Date) return value;
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(value);
}

/**
 * The LOCAL calendar date of a Date (default: now) as 'YYYY-MM-DD'.
 * Safe replacement for `date.toISOString().split('T')[0]`.
 */
export function localDateString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Format a DATE-only value for display without the UTC day-shift.
 * Default: "Jul 22, 2026". Pass Intl options to customize.
 */
export function formatDateOnly(value, options) {
  if (!value) return "-";
  return parseDateOnly(value).toLocaleDateString(
    "en-US",
    options || { year: "numeric", month: "short", day: "numeric" },
  );
}
